import {useEffect, useState} from "react";
import {Archive, Edit3, Plus, Send, X} from "lucide-react";
import {AdminTable} from "../../../shared/components/AdminTable";
import {DataState} from "../../../shared/components/DataState";
import {PageToolbar, ToolbarButton, ToolbarInput, ToolbarSelect} from "../../../shared/components/PageToolbar";
import {StatusBadge} from "../../../shared/components/StatusBadge";
import {asList, formatDateTime} from "../../../shared/utils/format";
import {createEmptyNewsDraft, newsDetailToDraft, newsDraftToPayload, validateNewsDraft} from "../contracts";
import {ConfirmDialog} from "./ConfirmDialog";
import {useTranslation, i18n} from "../../../shared/i18n";


const errorMessage = (error, fallback) => error?.response?.data?.message || error?.message || fallback;

export const NewsWorkspace = ({
    activeQuery,
    archiveQuery,
    filters,
    setFilters,
    canManage,
    countryId,
    activeCountryScopeRef,
    detailMutation,
    saveMutation,
    archiveMutation,
    result,
}) => {
    const {t} = useTranslation();
    const [listMode, setListMode] = useState("active");
    const [editorMode, setEditorMode] = useState(null);
    const [draft, setDraft] = useState(createEmptyNewsDraft);
    const [errors, setErrors] = useState({});
    const [archiveTarget, setArchiveTarget] = useState(null);
    const source = listMode === "archive" ? archiveQuery : activeQuery;
    const rows = asList(listMode === "archive" ? archiveQuery.data : activeQuery.data?.news ?? activeQuery.data);
    const cities = asList(activeQuery.data?.cities);
    const months = asList(activeQuery.data?.months);
    const years = asList(activeQuery.data?.years);

    const closeEditor = () => {
        setEditorMode(null);
        setDraft(createEmptyNewsDraft());
        setErrors({});
    };

    useEffect(() => {
        setEditorMode(null);
        setDraft(createEmptyNewsDraft());
        setErrors({});
    }, [countryId]);

    const openCreate = () => {
        setDraft(createEmptyNewsDraft());
        setErrors({});
        setEditorMode("create");
    };

    const openEdit = (article) => {
        setErrors({});
        const requestCountryId = countryId;
        detailMutation.mutate({articleId: article.id, requestCountryId}, {
            onSuccess: (detail) => {
                if (String(activeCountryScopeRef.current) !== String(requestCountryId)) return;
                setDraft(newsDetailToDraft(detail));
                setEditorMode("edit");
            },
        });
    };

    const submit = (event) => {
        event.preventDefault();
        const validation = validateNewsDraft(draft, {mode: editorMode});
        setErrors(validation);
        if (Object.keys(validation).length) return;
        saveMutation.mutate(newsDraftToPayload(draft, {mode: editorMode}), {onSuccess: closeEditor});
    };

    const columns = [
        {
            key: "title",
            label: t("sections.content.p262"),
            render: (row) => <div className="admin-content-title-cell"><strong>{row.title}</strong><span>{row.city === "-" ? i18n.t("sections.content.p263") : row.city}</span></div>,
        },
        {key: "created_at", label: t("sections.content.p264"), render: (row) => formatDateTime(row.created_at)},
        {
            key: "visibility",
            label: t("sections.content.p265"),
            render: (row) => <StatusBadge tone={row.is_shown ? "success" : "warning"}>{row.is_shown ? i18n.t("sections.content.p266") : i18n.t("sections.content.p267")}</StatusBadge>,
        },
        {
            key: "actions",
            label: t("sections.content.p155"),
            render: (row) => listMode === "archive" ? "—" : (
                <div className="content-row-actions">
                    <button aria-label={i18n.t("sections.content.p268", {v0: row.id})} className="admin-row-action" disabled={!canManage || detailMutation.isPending} onClick={() => openEdit(row)} type="button"><Edit3 size={14} /> {detailMutation.isPending ? i18n.t("sections.content.p269") : i18n.t("sections.content.p270")}</button>
                    <button aria-label={i18n.t("sections.content.p271", {v0: row.id})} className="admin-row-action" disabled={!canManage} onClick={() => setArchiveTarget(row)} type="button"><Archive size={14} /> {i18n.t("sections.content.p272")}</button>
                </div>
            ),
        },
    ];

    return (
        <div className="content-workspace">
            <div className="content-workspace-head">
                <div><h2>{t("sections.content.p11")}</h2><p>{t("sections.content.p273")}</p></div>
                <ToolbarButton aria-label={t("sections.content.p274")} disabled={!canManage} onClick={openCreate}><Plus size={15} /> {t("sections.content.p274")}</ToolbarButton>
            </div>

            <PageToolbar>
                <ToolbarInput aria-label={t("sections.content.p275")} label={t("sections.content.p174")} placeholder={t("sections.content.p276")} role="searchbox" value={filters.search} onChange={(event) => setFilters((current) => ({...current, search: event.target.value}))} />
                <ToolbarSelect aria-label={t("sections.content.p277")} label={t("sections.content.p277")} value={filters.city} onChange={(event) => setFilters((current) => ({...current, city: event.target.value}))}>
                    <option value="">{t("sections.content.p278")}</option>{cities.map((city) => <option key={city.id} value={city.name}>{city.name}</option>)}
                </ToolbarSelect>
                <ToolbarSelect aria-label={t("sections.content.p279")} label={t("sections.content.p279")} value={filters.month} onChange={(event) => setFilters((current) => ({...current, month: event.target.value}))}>
                    <option value="">{t("sections.content.p280")}</option>{months.map((month) => <option key={month.id} value={month.id}>{month.name}</option>)}
                </ToolbarSelect>
                <ToolbarSelect aria-label={t("sections.content.p281")} label={t("sections.content.p281")} value={filters.year} onChange={(event) => setFilters((current) => ({...current, year: event.target.value}))}>
                    <option value="">{t("sections.content.p280")}</option>{years.map((year) => <option key={year.id} value={year.id}>{year.name}</option>)}
                </ToolbarSelect>
                <ToolbarSelect aria-label={t("sections.content.p282")} label={t("sections.content.p283")} value={listMode} onChange={(event) => setListMode(event.target.value)}>
                    <option value="active">{t("sections.content.p284")}</option><option value="archive">{t("sections.content.p16")}</option>
                </ToolbarSelect>
            </PageToolbar>

            {result ? <p className={`content-result ${result.kind === "error" ? "is-error" : ""}`} role={result.kind === "error" ? "alert" : "status"}>{result.message}</p> : null}
            {detailMutation.isError ? <p className="content-result is-error" role="alert">{errorMessage(detailMutation.error, t("sections.content.p285"))}</p> : null}

            {editorMode ? (
                <form aria-label={editorMode === "edit" ? t("sections.content.p286") : t("sections.content.p274")} className="content-editor" onSubmit={submit}>
                    <div className="content-workspace-head">
                        <div>
                            <h3>{editorMode === "edit" ? t("sections.content.p287", {v0: draft.article_id}) : t("sections.content.p274")}</h3>
                            <p>{editorMode === "edit" ? t("sections.content.p288") : t("sections.content.p289")}</p>
                        </div>
                        <button aria-label={t("sections.content.p290")} className="content-icon-button" disabled={saveMutation.isPending} onClick={closeEditor} type="button"><X size={17} /></button>
                    </div>
                    {editorMode === "edit" ? (
                        <div className="content-editor-grid is-edit">
                            <TextField error={errors.title} label={t("sections.content.p276")} value={draft.title} onChange={(value) => setDraft((current) => ({...current, title: value}))} />
                            <TextArea error={errors.text} label={t("sections.content.p291")} value={draft.text} onChange={(value) => setDraft((current) => ({...current, text: value}))} />
                        </div>
                    ) : (
                        <div className="content-locale-columns">
                            <LocaleFields code="RU" draft={draft} errors={errors} prefix="ru" setDraft={setDraft} />
                            <LocaleFields code="KK" draft={draft} errors={errors} prefix="kk" setDraft={setDraft} />
                            <LocaleFields code="KY" draft={draft} errors={errors} prefix="ky" setDraft={setDraft} />
                        </div>
                    )}
                    <label className="admin-field">
                        <span>{t("sections.content.p292")}</span>
                        <select aria-label={t("sections.content.p292")} value={draft.city} onChange={(event) => setDraft((current) => ({...current, city: event.target.value}))}>
                            <option value="">{t("sections.content.p263")}</option>{cities.map((city) => <option key={city.id} value={city.name}>{city.name}</option>)}
                        </select>
                    </label>
                    {saveMutation.isError ? <p className="content-result is-error" role="alert">{errorMessage(saveMutation.error, t("sections.content.p293"))}</p> : null}
                    <div className="content-editor-actions">
                        <button className="admin-button admin-button-secondary" disabled={saveMutation.isPending} onClick={closeEditor} type="button">{t("sections.content.p180")}</button>
                        <button className="admin-button admin-button-primary" disabled={saveMutation.isPending} type="submit"><Send size={15} /> {saveMutation.isPending ? t("sections.content.p109") : editorMode === "edit" ? t("sections.content.p294") : t("sections.content.p295")}</button>
                    </div>
                </form>
            ) : null}

            {source.isError ? (
                <DataState title={t("sections.content.p296")} copy={errorMessage(source.error, t("sections.content.p297"))} />
            ) : (
                <AdminTable columns={columns} emptyText={listMode === "archive" ? t("sections.content.p298") : t("sections.content.p299")} loading={source.isLoading} rows={rows} />
            )}

            <ConfirmDialog
                copy={archiveTarget ? t("sections.content.p300", {v0: archiveTarget.title}) : ""}
                onCancel={() => setArchiveTarget(null)}
                onConfirm={() => archiveMutation.mutate(archiveTarget.id, {onSuccess: () => setArchiveTarget(null)})}
                open={Boolean(archiveTarget)}
                pending={archiveMutation.isPending}
                title={t("sections.content.p301")}
                confirmLabel={t("sections.content.p302")}
            />
        </div>
    );
};

const TextField = ({label, value, onChange, error}) => <label className="admin-field"><span>{label}</span><input aria-invalid={Boolean(error)} aria-label={label} value={value} onChange={(event) => onChange(event.target.value)} />{error ? <small className="content-field-error">{error}</small> : null}</label>;
const TextArea = ({label, value, onChange, error}) => <label className="admin-field"><span>{label}</span><textarea aria-invalid={Boolean(error)} aria-label={label} className="admin-textarea" value={value} onChange={(event) => onChange(event.target.value)} />{error ? <small className="content-field-error">{error}</small> : null}</label>;

const LocaleFields = ({code, prefix, draft, errors, setDraft}) => (
    <fieldset className="content-locale-fieldset">
        <legend>{code}</legend>
        <TextField error={errors[`${prefix}_title`]} label={i18n.t("sections.content.p303", {v0: code})} value={draft[`${prefix}_title`]} onChange={(value) => setDraft((current) => ({...current, [`${prefix}_title`]: value}))} />
        <TextArea error={errors[`${prefix}_text`]} label={i18n.t("sections.content.p304", {v0: code})} value={draft[`${prefix}_text`]} onChange={(value) => setDraft((current) => ({...current, [`${prefix}_text`]: value}))} />
    </fieldset>
);
